import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, ExternalLink, Plus } from 'lucide-react';
import type {
  CrewJob,
  CrewJobCommit,
  CrewJobCommitStatus,
} from '../../../types/crewJobs';
import {
  CREW_JOB_COMMIT_STATUS_LABELS,
  CREW_JOB_STATUS_LABELS,
} from '../../../types/crewJobs';
import type { ContractorApplicant } from '../../../types/contractorOnboard';
import { useCRM } from '../../../utils/CRMContext';
import { resolveCrewJobDates } from '../../../utils/crewJobsDates';
import { formatCrewJobHotelLine } from '../../../utils/crewJobsMap';
import { formatCrewJobPayBand, payBandFromJob } from '../../../utils/crewJobsPay';
import {
  createManualCrewJobCommit,
  deleteCrewJobCommit,
  formatCrewJobDateRange,
  setCrewJobStatus,
  subscribeCrewJobCommits,
  updateCrewJobCommit,
  writeCrewJobRosterCount,
} from '../../../utils/crewJobsFirestore';
import { countsTowardCrewRoster } from '../../../utils/crewJobsRoster';
import {
  contractorTrailBadge,
  normalizeContractorEmail,
  resumeApplicantStatus,
} from '../../../utils/contractorOnboard';
import {
  applicantByEmailMap,
  deleteContractorApplicant,
  ensureContractorApplicantFromCommit,
  subscribeContractorApplicants,
  updateContractorApplicantStatus,
} from '../../../utils/contractorOnboardFirestore';
import type { ContractorOnboardViewerOpen } from '../../../utils/contractorOnboardViewer';
import ContractorOnboardPanel from './ContractorOnboardPanel';
import CrewJobsAnalyticsPanel from './CrewJobsAnalyticsPanel';

type Props = {
  jobs: CrewJob[];
  selectedJobId: string | null;
  onSelectJobId: (id: string | null) => void;
  selectedApplicantId: string | null;
  onSelectApplicantId: (id: string | null) => void;
  onEditJob: (job: CrewJob) => void;
  canRelease: boolean;
  reviewerUid: string;
  onOpenPacketDoc?: (doc: ContractorOnboardViewerOpen) => void;
};

const CrewJobsPanel: React.FC<Props> = ({
  jobs,
  selectedJobId,
  onSelectJobId,
  selectedApplicantId,
  onSelectApplicantId,
  onEditJob,
  canRelease,
  reviewerUid,
  onOpenPacketDoc,
}) => {
  const { projectTasks, requestCrmCollections, crmUsers } = useCRM();
  const selected = jobs.find(j => j.id === selectedJobId) || null;
  const reviewerName = useMemo(() => {
    const row = crmUsers.find(u => u.uid === reviewerUid);
    return row?.displayName || row?.email || undefined;
  }, [crmUsers, reviewerUid]);
  const datesFor = (job: CrewJob) => formatCrewJobDateRange(resolveCrewJobDates(job, projectTasks));
  const selectedPay = selected ? formatCrewJobPayBand(payBandFromJob(selected)) : null;
  const selectedHotel = selected
    ? formatCrewJobHotelLine(selected.hotelName, selected.hotelLocation)
    : null;

  useEffect(() => {
    requestCrmCollections(['projectTasks']);
  }, [requestCrmCollections]);
  const [commits, setCommits] = useState<CrewJobCommit[]>([]);
  const [commitsReady, setCommitsReady] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [closedExpanded, setClosedExpanded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [applicants, setApplicants] = useState<ContractorApplicant[]>([]);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);

  useEffect(() => subscribeContractorApplicants(setApplicants), []);

  const applicantsByEmail = useMemo(() => applicantByEmailMap(applicants), [applicants]);

  useEffect(() => {
    setCommitsReady(false);
    setCommits([]);
    if (!selected?.id) return undefined;
    return subscribeCrewJobCommits(selected.id, rows => {
      setCommits(rows);
      setCommitsReady(true);
    });
  }, [selected?.id]);

  useEffect(() => {
    if (!selected || !commitsReady) return;
    const confirmed = commits.filter(c => countsTowardCrewRoster(c.status)).length;
    void writeCrewJobRosterCount(selected, confirmed);
  }, [selected, commits, commitsReady]);

  const boardUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/crew-jobs`
    : '/crew-jobs';

  const changeStatus = async (status: CrewJob['status']) => {
    if (!selected) return;
    setStatusBusy(true);
    try {
      await setCrewJobStatus(selected.id, status, reviewerUid, reviewerName);
    } finally {
      setStatusBusy(false);
    }
  };

  const openJobs = useMemo(() => jobs.filter(j => j.status !== 'closed'), [jobs]);
  const closedJobs = useMemo(() => jobs.filter(j => j.status === 'closed'), [jobs]);

  const updateCommitStatus = async (commit: CrewJobCommit, status: CrewJobCommitStatus) => {
    setConfirmError(null);
    try {
      await updateCrewJobCommit(commit.id, { status });
      const applicant = applicantsByEmail.get(normalizeContractorEmail(commit.email));
      if (applicant && applicant.status !== 'pooled') {
        if (status === 'declined') {
          await updateContractorApplicantStatus(applicant.id, 'declined');
        } else if (status === 'committed' && applicant.status === 'declined') {
          await updateContractorApplicantStatus(applicant.id, resumeApplicantStatus(applicant));
        }
      }
    } catch (e) {
      setConfirmError((e as Error).message);
    }
  };

  const restoreCandidate = async (commit: CrewJobCommit) => {
    setConfirmError(null);
    try {
      await updateCrewJobCommit(commit.id, { status: 'committed' });
      const applicant = applicantsByEmail.get(normalizeContractorEmail(commit.email));
      if (applicant && applicant.status === 'declined') {
        await updateContractorApplicantStatus(applicant.id, resumeApplicantStatus(applicant));
      }
    } catch (e) {
      setConfirmError((e as Error).message);
    }
  };

  const deletePooledApplicant = async (applicant: ContractorApplicant) => {
    const name = `${applicant.firstName} ${applicant.lastName}`.trim() || applicant.email;
    if (!window.confirm(`Remove ${name} from the 1099 pool? This deletes their crew applicant record.`)) {
      return;
    }
    setConfirmError(null);
    try {
      await deleteContractorApplicant(applicant.id);
      if (selectedApplicantId === applicant.id) {
        onSelectApplicantId(null);
      }
    } catch (e) {
      setConfirmError((e as Error).message);
    }
  };

  const deleteCandidate = async (commit: CrewJobCommit) => {
    const name = `${commit.firstName} ${commit.lastName}`.trim() || commit.email;
    if (!window.confirm(`Delete ${name} from this raise? Their onboarding trail will also be removed if they are not already in the pool.`)) {
      return;
    }
    setConfirmError(null);
    try {
      const applicant = applicantsByEmail.get(normalizeContractorEmail(commit.email));
      await deleteCrewJobCommit(commit.id);
      if (applicant && applicant.status !== 'pooled') {
        await deleteContractorApplicant(applicant.id);
      }
      if (applicant && selectedApplicantId === applicant.id) {
        onSelectApplicantId(null);
      }
    } catch (e) {
      setConfirmError((e as Error).message);
    }
  };

  const addPooledToJob = async (applicant: ContractorApplicant) => {
    if (!selected) return;
    setAddError(null);
    try {
      await createManualCrewJobCommit({
        job: selected,
        firstName: applicant.firstName,
        lastName: applicant.lastName,
        email: applicant.email,
        phone: applicant.phone || '—',
        contractorUserId: applicant.contractorUserId,
        note: 'Added from 1099 pool',
      });
    } catch (e) {
      setAddError((e as Error).message || 'Could not add to this raise');
    }
  };

  const addCommit = async () => {
    if (!selected) return;
    setAdding(true);
    setAddError(null);
    try {
      await createManualCrewJobCommit({
        job: selected,
        firstName,
        lastName,
        email,
        phone,
        note,
      });
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setNote('');
      setAddOpen(false);
    } catch (e) {
      setAddError((e as Error).message || 'Could not add commitment');
    } finally {
      setAdding(false);
    }
  };

  const poolNotOnJob = useMemo(() => {
    const onJob = new Set(commits.map(c => normalizeContractorEmail(c.email)));
    return applicants.filter(a => a.status === 'pooled' && !onJob.has(normalizeContractorEmail(a.email)));
  }, [applicants, commits]);

  return (
    <div className="people-hiring-grid people-hiring-grid--crew">
      <section className="people-hub-panel people-hiring-postings">
        <div className="people-hiring-section-head">
          <div className="crew-jobs-title-row">
            <h3>Crew jobs</h3>
            <div
              className="crew-jobs-analytics-hover"
              onMouseEnter={() => setAnalyticsOpen(true)}
              onMouseLeave={() => setAnalyticsOpen(false)}
              onFocus={() => setAnalyticsOpen(true)}
              onBlur={e => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setAnalyticsOpen(false);
                }
              }}
            >
              <button
                type="button"
                className="crew-jobs-analytics-trigger"
                aria-label="Board traffic analytics"
                aria-expanded={analyticsOpen}
                aria-haspopup="dialog"
              >
                <BarChart3 size={15} />
              </button>
              {analyticsOpen && (
                <div className="crew-jobs-analytics-popover" role="dialog" aria-label="Board traffic">
                  <CrewJobsAnalyticsPanel compact />
                </div>
              )}
            </div>
          </div>
          <div className="crew-jobs-head-actions">
            <a className="pm-btn pm-btn-ghost" href={boardUrl} target="_blank" rel="noreferrer">
              <ExternalLink size={14} /> Open board
            </a>
            <span className="people-hiring-muted">
              {openJobs.length} open
              {closedJobs.length > 0 ? ` · ${closedJobs.length} closed` : ''}
            </span>
          </div>
        </div>
        {jobs.length === 0 ? (
          <div className="people-hiring-empty">
            <p>No raise jobs yet. Draft one, then a PM or COO can release it.</p>
          </div>
        ) : (
          <>
            <div className="people-hiring-table-wrap crew-jobs-table-wrap">
              <table className="people-hiring-table crew-jobs-table">
                <thead>
                  <tr>
                    <th>Raise</th>
                    <th>Dates</th>
                    <th>Crew</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {openJobs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="people-hiring-muted">
                        No open raises. Closed jobs are below.
                      </td>
                    </tr>
                  ) : (
                    openJobs.map(job => (
                      <tr
                        key={job.id}
                        className={selectedJobId === job.id ? 'people-hiring-row-selected' : undefined}
                        onClick={() => onSelectJobId(job.id)}
                      >
                        <td className="crew-jobs-table-title" title={job.title}>
                          {job.title}
                        </td>
                        <td className="crew-jobs-table-nowrap" title={datesFor(job)}>
                          {compactCrewDates(datesFor(job))}
                        </td>
                        <td className="crew-jobs-table-nowrap">
                          {job.commitCount}/{job.crewNeeded}
                        </td>
                        <td>
                          <span className={`people-hiring-status people-hiring-status--${job.status}`}>
                            {CREW_JOB_STATUS_LABELS[job.status]}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="pm-btn pm-btn-ghost crew-jobs-table-edit"
                            onClick={e => {
                              e.stopPropagation();
                              onEditJob(job);
                            }}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {closedJobs.length > 0 && (
              <div className="crew-jobs-closed-block">
                <button
                  type="button"
                  className="crew-jobs-closed-toggle"
                  aria-expanded={closedExpanded}
                  onClick={() => setClosedExpanded(v => !v)}
                >
                  {closedExpanded ? 'Hide' : 'Show'} closed ({closedJobs.length})
                </button>
                {closedExpanded && (
                  <div className="people-hiring-table-wrap crew-jobs-table-wrap">
                    <table className="people-hiring-table crew-jobs-table">
                      <tbody>
                        {closedJobs.map(job => (
                          <tr
                            key={job.id}
                            className={
                              selectedJobId === job.id ? 'people-hiring-row-selected' : undefined
                            }
                            onClick={() => onSelectJobId(job.id)}
                          >
                            <td className="crew-jobs-table-title" title={job.title}>
                              {job.title}
                            </td>
                            <td className="crew-jobs-table-nowrap" title={datesFor(job)}>
                              {compactCrewDates(datesFor(job))}
                            </td>
                            <td className="crew-jobs-table-nowrap">
                              {job.commitCount}/{job.crewNeeded}
                            </td>
                            <td>
                              <span className="people-hiring-status people-hiring-status--closed">
                                Closed
                                {job.closedByName
                                  ? ` · ${job.closedByName}`
                                  : ''}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="pm-btn pm-btn-ghost crew-jobs-table-edit"
                                onClick={e => {
                                  e.stopPropagation();
                                  onEditJob(job);
                                }}
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </section>

      <section className="people-hub-panel people-hiring-apps crew-candidates-card">
        {!selected ? (
          <div className="people-hiring-empty">
            <p>Select a crew job to work candidates through 1099 onboarding.</p>
          </div>
        ) : (
          <>
            <div className="people-hiring-section-head">
              <h3>{selected.title}</h3>
              <span className="people-hiring-muted">
                {selected.status === 'filled'
                  || (selected.crewNeeded > 0 && selected.commitCount >= selected.crewNeeded)
                  ? 'Filled'
                  : `${selected.commitCount} / ${selected.crewNeeded} on roster`}
              </span>
            </div>
            <div className="people-hiring-muted" style={{ marginBottom: 10 }}>
              {datesFor(selected)}
              {selected.location ? ` · ${selected.location}` : ''}
              {selectedPay ? ` · ${selectedPay}` : ''}
              {selectedHotel ? ` · Hotel: ${selectedHotel}` : ''}
            </div>
            <div className="people-hiring-resume-actions" style={{ marginBottom: 12 }}>
              {canRelease && selected.status === 'draft' && (
                <button
                  type="button"
                  className="ops-new-btn"
                  disabled={statusBusy}
                  onClick={() => void changeStatus('released')}
                >
                  Release
                </button>
              )}
              {canRelease && (selected.status === 'released' || selected.status === 'filled') && (
                <button
                  type="button"
                  className="pm-btn pm-btn-ghost"
                  disabled={statusBusy}
                  onClick={() => void changeStatus('closed')}
                  title="Take this raise off the public board"
                >
                  Close
                </button>
              )}
              {canRelease && selected.status === 'closed' && (
                <button
                  type="button"
                  className="pm-btn pm-btn-secondary"
                  disabled={statusBusy}
                  onClick={() => void changeStatus('released')}
                  title={
                    selected.closedAt
                      ? `Closed${selected.closedByName ? ` by ${selected.closedByName}` : ''}${
                          selected.closedAt ? ` on ${selected.closedAt.toLocaleDateString()}` : ''
                        }`
                      : 'Reopen this raise on the open list and public board'
                  }
                >
                  Reopen
                </button>
              )}
              <button
                type="button"
                className="pm-btn pm-btn-secondary"
                onClick={() => setAddOpen(o => !o)}
                title="Manually add someone to this raise by name and email (they do not need to commit on the public board first)"
              >
                <Plus size={14} /> Add person to raise
              </button>
            </div>

            {addOpen && (
              <div className="people-hiring-resume" style={{ marginBottom: 12 }}>
                <div className="people-hiring-row">
                  <div className="people-hiring-field">
                    <label>First name</label>
                    <input value={firstName} onChange={e => setFirstName(e.target.value)} />
                  </div>
                  <div className="people-hiring-field">
                    <label>Last name</label>
                    <input value={lastName} onChange={e => setLastName(e.target.value)} />
                  </div>
                </div>
                <div className="people-hiring-row">
                  <div className="people-hiring-field">
                    <label>Email</label>
                    <input value={email} onChange={e => setEmail(e.target.value)} />
                  </div>
                  <div className="people-hiring-field">
                    <label>Phone</label>
                    <input value={phone} onChange={e => setPhone(e.target.value)} />
                  </div>
                </div>
                <div className="people-hiring-field">
                  <label>Note</label>
                  <input value={note} onChange={e => setNote(e.target.value)} />
                </div>
                {addError && <p className="people-hiring-resume-error">{addError}</p>}
                {confirmError && <p className="people-hiring-resume-error">{confirmError}</p>}
                <div className="people-hiring-resume-actions">
                  <button type="button" className="ops-new-btn" disabled={adding} onClick={() => void addCommit()}>
                    {adding ? 'Saving…' : 'Add to raise'}
                  </button>
                </div>
              </div>
            )}

            {confirmError && <p className="people-hiring-resume-error">{confirmError}</p>}
            {addError && !addOpen && <p className="people-hiring-resume-error">{addError}</p>}

            {commits.length === 0 ? (
              <div className="people-hiring-empty">
                <p>No candidates on this raise yet.</p>
              </div>
            ) : (
              <div className="crew-candidate-list">
                {commits.map(commit => {
                  const applicant = applicantsByEmail.get(normalizeContractorEmail(commit.email));
                  const trail = contractorTrailBadge(applicant);
                  const expanded = Boolean(
                    applicant && selectedApplicantId === applicant.id,
                  );
                  const offRaise = commit.status === 'declined' || commit.status === 'withdrawn';
                  const inPool = applicant?.status === 'pooled';
                  const onRoster = commit.status === 'confirmed';
                  const canConfirmToRoster = Boolean(
                    inPool && !onRoster && !offRaise,
                  );
                  const statusTone = onRoster
                    ? 'published'
                    : offRaise
                      ? 'closed'
                      : 'draft';
                  return (
                    <div
                      key={commit.id}
                      className={`crew-candidate-row ${expanded ? 'is-open' : ''} ${offRaise ? 'is-off' : ''} ${inPool ? 'is-pooled' : ''}`}
                    >
                      <div className="crew-candidate-row-main">
                        <button
                          type="button"
                          className="crew-candidate-row-toggle"
                          onClick={() => {
                            if (applicant) {
                              onSelectApplicantId(expanded ? null : applicant.id);
                              return;
                            }
                            void ensureContractorApplicantFromCommit({
                              commitId: commit.id,
                              jobId: commit.jobId,
                              jobTitle: commit.jobTitle,
                              firstName: commit.firstName,
                              lastName: commit.lastName,
                              email: commit.email,
                              phone: commit.phone,
                            }).then(row => onSelectApplicantId(row.id));
                          }}
                        >
                          <span className="crew-candidate-identity">
                            <span className="crew-candidate-name-line">
                              <strong className="crew-candidate-name">
                                {commit.firstName} {commit.lastName}
                              </strong>
                              {inPool && !onRoster && (
                                <span className="crew-candidate-badge crew-candidate-badge--pool">
                                  In pool
                                </span>
                              )}
                              {onRoster && (
                                <span className="crew-candidate-badge crew-candidate-badge--roster">
                                  On roster
                                </span>
                              )}
                            </span>
                            <span className="crew-candidate-email">{commit.email}</span>
                          </span>
                        </button>
                        <span className="crew-candidate-row-meta">
                          {canConfirmToRoster && (
                            <button
                              type="button"
                              className="ops-new-btn crew-candidate-confirm-btn"
                              onClick={() => void updateCommitStatus(commit, 'confirmed')}
                            >
                              Confirm to Roster
                            </button>
                          )}
                          {onRoster && (
                            <button
                              type="button"
                              className="pm-btn pm-btn-ghost crew-candidate-remove-roster-btn"
                              onClick={() => {
                                const name = `${commit.firstName} ${commit.lastName}`.trim() || commit.email;
                                if (
                                  !window.confirm(
                                    `Remove ${name} from this job's roster? They stay in the 1099 pool (contract record kept).`,
                                  )
                                ) {
                                  return;
                                }
                                void updateCommitStatus(commit, 'committed');
                              }}
                            >
                              Remove from roster
                            </button>
                          )}
                          {!offRaise && !inPool && applicant && (
                            <span className={`crew-candidate-stage crew-candidate-stage--${trail.state}`}>
                              {trail.label}
                            </span>
                          )}
                          {!onRoster && (
                            <span className={`people-hiring-status people-hiring-status--${statusTone}`}>
                              {CREW_JOB_COMMIT_STATUS_LABELS[commit.status]}
                            </span>
                          )}
                        </span>
                      </div>
                      {expanded && applicant && (
                        <ContractorOnboardPanel
                          applicant={applicant}
                          job={selected}
                          commit={commit}
                          reviewerUid={reviewerUid}
                          onOpenPacketDoc={onOpenPacketDoc}
                          onConfirm={() => void updateCommitStatus(commit, 'confirmed')}
                          onDecline={() => void updateCommitStatus(commit, 'declined')}
                          onRestore={() => void restoreCandidate(commit)}
                          onDelete={() => void deleteCandidate(commit)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {poolNotOnJob.length > 0 && (
              <div className="crew-candidate-pool">
                <h4 className="people-hiring-muted">1099 pool</h4>
                {poolNotOnJob.map(row => (
                  <div key={row.id} className="crew-candidate-row is-pooled">
                    <div className="crew-candidate-row-main">
                      <span className="crew-candidate-identity">
                        <span className="crew-candidate-name-line">
                          <strong className="crew-candidate-name">{row.firstName} {row.lastName}</strong>
                          <span className="crew-candidate-badge crew-candidate-badge--pool">In pool</span>
                        </span>
                        <span className="crew-candidate-email">{row.email}</span>
                      </span>
                      <button
                        type="button"
                        className="pm-btn pm-btn-ghost"
                        onClick={() => void addPooledToJob(row)}
                      >
                        Add to raise
                      </button>
                      <button
                        type="button"
                        className="pm-btn pm-btn-ghost crew-pool-delete"
                        onClick={() => void deletePooledApplicant(row)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default CrewJobsPanel;

/** Shorten "Oct 19, 2026 – Oct 23, 2026" → "Oct 19–23, 2026" when same month/year. */
function compactCrewDates(range: string): string {
  const match = range.match(
    /^([A-Za-z]{3}) (\d{1,2}), (\d{4}) – ([A-Za-z]{3}) (\d{1,2}), (\d{4})$/,
  );
  if (!match) return range;
  const [, m1, d1, y1, m2, d2, y2] = match;
  if (m1 === m2 && y1 === y2) return `${m1} ${d1}–${d2}, ${y1}`;
  if (y1 === y2) return `${m1} ${d1} – ${m2} ${d2}, ${y1}`;
  return range;
}
